import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bell, CheckCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SwipeToRead from "@/components/notifications/SwipeToRead";
import NotifPager from "@/components/notifications/NotifPager";
import { QuickLinks } from "@/components/notifications/NotifRow";
import { notifBase, notifLink, refreshBadges, requestLink, requestTag, timeAgo } from "@/lib/notifications";
import { shortenNos } from "@/lib/caseNo";

const PER = 5;

function DashNotifRow({ n, tag, onRead, onOpen }) {
  return (
    <SwipeToRead onSwiped={() => onRead(n)} onSwipedRight={() => onOpen(n)} testId={`dash-notif-swipe-${n.id}`}>
      <div role="button" tabIndex={0} onClick={() => onOpen(n)} title="Enter to open · Delete to mark as read"
        onKeyDown={(e) => { if (e.key === "Enter") onOpen(n); if (e.key === "Delete" || e.key === "Backspace") onRead(n); }}
        className="group flex cursor-pointer items-start gap-3 px-2 py-3 transition-colors hover:bg-slate-50" data-testid={`dash-notif-${n.id}`}>
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800">{n.title}</p>
            {tag && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tag.cls}`} data-testid={`dash-notif-tag-${n.id}`}>{tag.label}</span>}
          </div>
          <p className="text-sm text-slate-500">{shortenNos(n.message)}</p>
          {n.links?.length > 0 && <QuickLinks n={n} onOpen={onOpen} />}
          <p className="mt-0.5 text-xs text-slate-400">{timeAgo(n.created_at)}</p>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5" />
      </div>
    </SwipeToRead>
  );
}

// Dashboard card with every unread notification (5 per page). Swipe/drag left = read (archive), right = open.
export default function DashboardNotificationsCard({ title = "Notifications", tick, testId = "recent-notifications-card" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hash, key } = useLocation();
  const ref = useRef(null);
  const base = notifBase(user?.role);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get("/notifications/paged", { params: { page, per_page: PER, only_unread: true } })
    .then(({ data }) => { setData(data); if (data.page !== page) setPage(data.page); }).catch(() => {}), [page]);
  useEffect(() => { load(); }, [load, tick]);
  // Bell → #notifications: keep the card in view while the rest of the dashboard finishes loading (stops on user scroll).
  useEffect(() => {
    if (hash !== "#notifications") return undefined;
    const go = () => ref.current?.scrollIntoView({ block: "start" });
    go();
    const ro = new ResizeObserver(go);
    ro.observe(document.body);
    const stop = () => { ro.disconnect(); ["wheel", "touchstart", "keydown"].forEach((e) => window.removeEventListener(e, stop)); };
    ["wheel", "touchstart", "keydown"].forEach((e) => window.addEventListener(e, stop, { passive: true }));
    const t = setTimeout(stop, 2000);
    return () => { clearTimeout(t); stop(); };
  }, [hash, key]);

  const read = async (n) => { try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } refreshBadges(); };
  const onRead = async (n) => { await read(n); load(); };
  const onOpen = async (n, to) => {
    await read(n);
    const tag = requestTag(n, user?.role);
    const dest = to || (tag ? requestLink(n) : notifLink(n, user?.role));
    navigate(dest || `${base}/notifications?focus=${n.id}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All notifications moved to the archive");
    } catch { toast.error("Couldn't mark notifications as read"); }
    refreshBadges(); setPage(1); load();
  };

  // Hidden entirely until there's at least one active (unread) notification.
  if (!data || (data.unread === 0 && data.items.length === 0)) {
    return <div ref={ref} id="notifications" className="scroll-mt-20" data-testid={`${testId}-wrap`} />;
  }

  return (
    <div ref={ref} id="notifications" className="mt-6 scroll-mt-20" data-testid={`${testId}-wrap`}>
      {data?.unread > 0 && (
        <div className="mb-2 flex justify-end">
          <button type="button" onClick={markAll} data-testid="dashboard-mark-all-read"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 active:scale-95">
            <CheckCheck className="h-3.5 w-3.5" /> Mark All Read
          </button>
        </div>
      )}
    <Card className="border-slate-200 p-6 shadow-sm" data-testid={testId}>
      <Link to={`${base}/notifications`} className="group flex w-fit items-center gap-2" data-testid="recent-notifications-link">
        <Bell className="h-5 w-5 text-slate-400" />
        <h3 className="font-heading text-lg font-semibold text-slate-800">{title}</h3>
        {data?.unread > 0 && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700 tabular-nums" data-testid="recent-notifications-unread">{data.unread}</span>}
        <ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5" />
      </Link>
      <p className="mb-3 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400" data-testid="notifications-swipe-instructions">
        <span className="inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Swipe left to mark as read</span>
        <span className="inline-flex items-center gap-1">Swipe right to open <ArrowRight className="h-3.5 w-3.5" /></span>
      </p>
      {data && data.items.length === 0 ? (
        <p className="py-4 text-sm text-slate-400" data-testid="recent-notifications-empty">No unread notifications.</p>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="recent-notifications-list">
          {data?.items.map((n) => <DashNotifRow key={n.id} n={n} tag={requestTag(n, user?.role)} onRead={onRead} onOpen={onOpen} />)}
        </div>
      )}
      {data && <div className="mt-3"><NotifPager page={data.page} pages={data.pages} label={`Unread · ${data.page} / ${data.pages}`} onPage={setPage} testId="recent-notifications" /></div>}
    </Card>
    </div>
  );
}
